// src/components/ProblemManager.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Loader2, Plus, Edit, Trash2, Save, XCircle, ChevronDown, ChevronUp, CheckCircle, Upload, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '../lib/utils';

// Import Firestore functions
import { db, collection, doc, setDoc, deleteDoc, getDocs, Timestamp, getDoc, query, orderBy, runTransaction } from '../firebase/firebase';

const ProblemManager = ({ onProblemAddedOrUpdated, initialProblems = [], problemToEdit = null, onEditComplete = () => { }, isTeacher = false, onClose = null }) => {
    // State for the form fields of a new/edited problem
    const [problemForm, setProblemForm] = useState({
        id: '',
        title: '',
        description: '',
        example: '',
        templateCode: '    public boolean sleepIn(boolean weekday, boolean vacation) {\n        // Your code here\n        return false;\n    }',
        methodName: '',
        tests: [{ input: '[]', expected: 'null' }],
        category: '',
        order: 0
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [problemsList, setProblemsList] = useState(initialProblems);

    // State for Bulk Import
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [bulkJsonInput, setBulkJsonInput] = useState('');
    const [bulkImportLoading, setBulkImportLoading] = useState(false);
    const [bulkImportError, setBulkImportError] = useState(null);
    const [bulkImportSuccess, setBulkImportSuccess] = useState('');

    // Security constants
    const MAX_JSON_SIZE = 5 * 1024 * 1024; // 5MB limit for JSON input
    const MAX_PROBLEMS_PER_IMPORT = 1000; // Maximum number of problems in a single import

    // State for category display order
    const [categoryDisplayOrders, setCategoryDisplayOrders] = useState([]);
    // State to manage expanded/collapsed categories in problem list
    const [expandedCategories, setExpandedCategories] = useState({});

    // Helper functions for hundreds-based ordering system
    // Each category gets a base number (0, 100, 200, 300, etc.)
    // Problems within a category get: categoryBase + positionInCategory

    // Get the category base order (e.g., category at position 0 = 0, position 1 = 100, position 2 = 200)
    const getCategoryBaseOrder = useCallback((category) => {
        if (!category) return 0; // Uncategorized gets base 0

        const categoryOrder = categoryDisplayOrders.find(c => c.id === category);
        if (categoryOrder) {
            return (categoryOrder.order || 0) * 100;
        }
        return 0;
    }, [categoryDisplayOrders]);

    // Calculate the full order number for a problem
    const calculateProblemOrder = useCallback((category, positionInCategory) => {
        const baseOrder = getCategoryBaseOrder(category);
        return baseOrder + positionInCategory;
    }, [getCategoryBaseOrder]);

    // Get the next available position within a category
    const getNextPositionInCategory = useCallback((category) => {
        const categoryProblems = problemsList.filter(p => (p.category || null) === (category || null));
        if (categoryProblems.length === 0) return 0;

        const baseOrder = getCategoryBaseOrder(category);
        const positions = categoryProblems.map(p => (p.order || 0) - baseOrder);
        return Math.max(...positions) + 1;
    }, [problemsList, getCategoryBaseOrder]);

    // Security helper functions

    // Sanitize text input to prevent XSS attacks
    const sanitizeText = useCallback((text) => {
        if (typeof text !== 'string') return text;

        // Remove potentially dangerous HTML/script tags and attributes
        return text
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
            .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove event handlers like onclick, onerror
            .replace(/javascript:/gi, '')
            .trim();
    }, []);

    // Validate problem ID to prevent path traversal attacks
    const validateProblemId = useCallback((id) => {
        if (!id || typeof id !== 'string') {
            return { valid: false, error: 'Problem ID must be a non-empty string' };
        }

        // Check for path traversal attempts
        if (id.includes('..') || id.includes('/') || id.includes('\\')) {
            return { valid: false, error: 'Problem ID contains invalid characters (path traversal attempt detected)' };
        }

        // Check for null bytes
        if (id.includes('\0')) {
            return { valid: false, error: 'Problem ID contains null bytes' };
        }

        // Ensure ID only contains safe characters (alphanumeric, hyphens, underscores)
        const safeIdPattern = /^[a-zA-Z0-9_-]+$/;
        if (!safeIdPattern.test(id)) {
            return { valid: false, error: 'Problem ID must contain only letters, numbers, hyphens, and underscores' };
        }

        // Check length constraints
        if (id.length > 100) {
            return { valid: false, error: 'Problem ID is too long (maximum 100 characters)' };
        }

        return { valid: true };
    }, []);

    // Sanitize problem object to prevent XSS in all text fields
    const sanitizeProblem = useCallback((problem) => {
        return {
            ...problem,
            title: sanitizeText(problem.title),
            description: sanitizeText(problem.description),
            example: sanitizeText(problem.example),
            templateCode: problem.templateCode, // Keep code as-is for functionality
            methodName: sanitizeText(problem.methodName),
            category: problem.category ? sanitizeText(problem.category) : null
        };
    }, [sanitizeText]);

    // Effect to update local problems list when initialProblems prop changes
    useEffect(() => {
        setProblemsList(initialProblems);
    }, [initialProblems]);

    // Effect to handle problemToEdit prop
    useEffect(() => {
        if (problemToEdit) {
            try {
                handleEditProblemClick(problemToEdit);
                setTimeout(() => {
                    onEditComplete();
                }, 100);
            } catch (error) {
                console.error("Error setting problem to edit:", error);
                setError(`Failed to load problem for editing: ${error.message}`);
                onEditComplete();
            }
        }
    }, [problemToEdit, onEditComplete]);

    // Effect to fetch category display orders
    useEffect(() => {
        const fetchCategoryOrders = async () => {
            try {
                const categoriesColRef = collection(db, 'category_display_orders');
                const q = query(categoriesColRef, orderBy('order', 'asc'));
                const querySnapshot = await getDocs(q);
                const loadedOrders = [];
                querySnapshot.forEach(doc => {
                    loadedOrders.push({ id: doc.id, ...doc.data() });
                });
                setCategoryDisplayOrders(loadedOrders);
            } catch (fetchError) {
                console.error("Error fetching category orders:", fetchError);
                setError(`Failed to load category orders: ${fetchError.message}`);
            }
        };

        fetchCategoryOrders();
    }, [onProblemAddedOrUpdated]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setProblemForm(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleEditProblemClick = useCallback((problemToEdit) => {
        try {
            const testsForForm = (problemToEdit.tests || []).map(test => {
                // Deserialize test inputs when loading for editing
                const deserializedInput = (test.input || []).map(param => {
                    // Check if this parameter is a serialized array
                    if (param && typeof param === 'object' && param.__type === 'serialized_array') {
                        // Deserialize the nested array from JSON string
                        try {
                            return JSON.parse(param.value);
                        } catch (parseError) {
                            console.error("Error deserializing array:", parseError);
                            // Fall back to empty array if deserialization fails
                            return [];
                        }
                    }
                    // Keep non-serialized parameters as-is
                    return param;
                });

                // Deserialize expected value if it's a serialized array
                let deserializedExpected = test.expected !== undefined ? test.expected : 'null';
                if (test.expected && typeof test.expected === 'object' && test.expected.__type === 'serialized_array') {
                    try {
                        deserializedExpected = JSON.parse(test.expected.value);
                    } catch (parseError) {
                        console.error("Error deserializing expected array:", parseError);
                        deserializedExpected = [];
                    }
                }

                return {
                    input: JSON.stringify(deserializedInput),
                    expected: JSON.stringify(deserializedExpected)
                };
            });

            setProblemForm({
                id: problemToEdit.id || '',
                title: problemToEdit.title || '',
                description: problemToEdit.description || '',
                example: problemToEdit.example || '',
                templateCode: problemToEdit.templateCode || '',
                methodName: problemToEdit.methodName || '',
                tests: testsForForm.length > 0 ? testsForForm : [{ input: '[]', expected: 'null' }],
                category: problemToEdit.category || '',
                order: problemToEdit.order || 0
            });
            setEditMode(true);
            setSuccessMessage('');
            setError(null);
        } catch (error) {
            console.error("Error in handleEditProblemClick:", error);
            setError(`Failed to load problem for editing: ${error.message}`);
        }
    }, []);

    const handleTestCaseChange = useCallback((index, field, value) => {
        const newTests = [...problemForm.tests];
        newTests[index] = { ...newTests[index], [field]: value };
        setProblemForm(prev => ({ ...prev, tests: newTests }));
    }, [problemForm.tests]);

    const addTestCase = useCallback(() => {
        setProblemForm(prev => ({
            ...prev,
            tests: [...prev.tests, { input: '[]', expected: 'null' }]
        }));
    }, []);

    const removeTestCase = useCallback((indexToRemove) => {
        setProblemForm(prev => ({
            ...prev,
            tests: prev.tests.filter((_, index) => index !== indexToRemove)
        }));
    }, []);

    // Validation function for individual problems (Task 4)
    const validateProblemStructure = useCallback((problem) => {
        const errors = [];

        // Check required fields
        if (!problem || typeof problem !== 'object') {
            return ['Problem must be a valid object'];
        }

        // Validate title (required)
        if (!problem.title || typeof problem.title !== 'string' || problem.title.trim() === '') {
            errors.push('Title is required and must be a non-empty string');
        }

        // Validate description (required)
        if (!problem.description || typeof problem.description !== 'string' || problem.description.trim() === '') {
            errors.push('Description is required and must be a non-empty string');
        }

        // Validate example (required)
        if (!problem.example || typeof problem.example !== 'string' || problem.example.trim() === '') {
            errors.push('Example is required and must be a non-empty string');
        }

        // Validate templateCode (required)
        if (!problem.templateCode || typeof problem.templateCode !== 'string' || problem.templateCode.trim() === '') {
            errors.push('Template code is required and must be a non-empty string');
        }

        // Validate methodName (required and must be valid identifier)
        if (!problem.methodName || typeof problem.methodName !== 'string' || problem.methodName.trim() === '') {
            errors.push('Method name is required and must be a non-empty string');
        } else {
            // Check if methodName is a valid JavaScript/Java identifier
            const methodNamePattern = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;
            if (!methodNamePattern.test(problem.methodName.trim())) {
                errors.push('Method name must be a valid identifier (letters, numbers, underscore, dollar sign; cannot start with number)');
            }
        }

        // Validate tests array (required)
        if (!problem.tests || !Array.isArray(problem.tests)) {
            errors.push('Tests must be an array');
        } else if (problem.tests.length === 0) {
            errors.push('Tests array cannot be empty - at least one test case is required');
        } else {
            // Validate each test case structure
            problem.tests.forEach((test, index) => {
                if (!test || typeof test !== 'object') {
                    errors.push(`Test case ${index + 1}: must be an object`);
                } else {
                    // Check for input and expected properties
                    if (!test.hasOwnProperty('input')) {
                        errors.push(`Test case ${index + 1}: missing 'input' property`);
                    }
                    if (!test.hasOwnProperty('expected')) {
                        errors.push(`Test case ${index + 1}: missing 'expected' property`);
                    }
                }
            });
        }

        // Validate optional fields if present
        if (problem.id !== undefined && (typeof problem.id !== 'string' || problem.id.trim() === '')) {
            errors.push('ID must be a non-empty string if provided');
        } else if (problem.id !== undefined) {
            // Validate ID for security (path traversal prevention)
            const idValidation = validateProblemId(problem.id.trim());
            if (!idValidation.valid) {
                errors.push(`ID validation failed: ${idValidation.error}`);
            }
        }

        if (problem.category !== undefined && problem.category !== null && (typeof problem.category !== 'string' || problem.category.trim() === '')) {
            errors.push('Category must be a non-empty string if provided');
        }

        if (problem.order !== undefined && (typeof problem.order !== 'number' || problem.order < 0)) {
            errors.push('Order must be a non-negative number if provided');
        }

        return errors;
    }, [validateProblemId]);

    const validateAndParseProblemForm = useCallback((formToValidate) => {
        const parsedTests = formToValidate.tests.map(test => {
            try {
                // Step 1: Pre-process Java array syntax - convert {} to []
                let inputStr = test.input;
                if (typeof inputStr === 'string') {
                    inputStr = inputStr.replace(/\{/g, '[').replace(/\}/g, ']');
                }
                
                // Step 2: Parse the input
                let parsedInput = JSON.parse(inputStr);
                if (!Array.isArray(parsedInput)) {
                    if (typeof parsedInput === 'number' && inputStr.includes('.')) {
                        parsedInput = [parseFloat(inputStr)];
                    } else {
                        parsedInput = [parsedInput];
                    }
                } else {
                    parsedInput = parsedInput.map((item, index) => {
                        if (typeof item === 'number') {
                            const originalStr = inputStr.replace(/[\[\]]/g, '').split(',')[index]?.trim();
                            if (originalStr && originalStr.includes('.')) {
                                return parseFloat(originalStr);
                            }
                        }
                        return item;
                    });
                }
                
                // Step 3: Serialize nested arrays to avoid Firestore nested array limitation
                const serializedInput = parsedInput.map(param => {
                    if (Array.isArray(param)) {
                        // This is a nested array - serialize it
                        return {
                            __type: 'serialized_array',
                            value: JSON.stringify(param)
                        };
                    }
                    return param; // Keep primitives as-is
                });
                
                // Step 4: Parse and serialize expected value if it's an array
                const parsedExpected = JSON.parse(test.expected);
                let serializedExpected = parsedExpected;
                if (Array.isArray(parsedExpected)) {
                    // Expected value is an array - serialize it to avoid Firestore nested array limitation
                    serializedExpected = {
                        __type: 'serialized_array',
                        value: JSON.stringify(parsedExpected)
                    };
                }
                
                return { input: serializedInput, expected: serializedExpected };
            } catch (e) {
                let inputVal;
                try {
                    // Pre-process Java array syntax for fallback parsing too
                    let inputStr = test.input;
                    if (typeof inputStr === 'string') {
                        inputStr = inputStr.replace(/\{/g, '[').replace(/\}/g, ']');
                    }
                    inputVal = JSON.parse(inputStr);
                } catch {
                    if (typeof test.input === 'string') {
                        inputVal = test.input.split(',').map(s => {
                            const trimmed = s.trim();
                            if (!isNaN(Number(trimmed)) && trimmed !== '') return Number(trimmed);
                            if (trimmed.toLowerCase() === 'true') return true;
                            if (trimmed.toLowerCase() === 'false') return false;
                            if (trimmed.toLowerCase() === 'null') return null;
                            return trimmed;
                        });
                    } else {
                        inputVal = test.input;
                    }
                }
                let expectedVal;
                try {
                    expectedVal = JSON.parse(test.expected);
                } catch {
                    expectedVal = typeof test.expected === 'string' ? test.expected.trim() : test.expected;
                    if (!isNaN(Number(expectedVal)) && expectedVal !== '') expectedVal = Number(expectedVal);
                    else if (expectedVal.toLowerCase() === 'true') expectedVal = true;
                    else if (expectedVal.toLowerCase() === 'false') expectedVal = false;
                    else if (expectedVal.toLowerCase() === 'null') expectedVal = null;
                }
                return { input: inputVal, expected: expectedVal };
            }
        });

        const problemId = formToValidate.id || formToValidate.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        if (!problemId) {
            throw new Error("Problem title cannot be empty. It's used to generate an ID.");
        }

        return {
            id: problemId,
            title: formToValidate.title.trim(),
            description: formToValidate.description.trim(),
            example: formToValidate.example.trim(),
            templateCode: formToValidate.templateCode.trim(),
            methodName: formToValidate.methodName.trim(),
            tests: parsedTests,
            category: formToValidate.category.trim() || null,
            order: typeof formToValidate.order === 'number' ? formToValidate.order : 0
        };
    }, []);

    const resetForm = useCallback(() => {
        setProblemForm({
            id: '',
            title: '',
            description: '',
            example: '',
            templateCode: '    public boolean sleepIn(boolean weekday, boolean vacation) {\n        // Your code here\n        return false;\n    }',
            methodName: '',
            tests: [{ input: '[]', expected: 'null' }],
            category: '',
            order: 0
        });
        setEditMode(false);
        setError(null);
        setSuccessMessage('');
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        // Security check: Verify teacher permission
        if (!isTeacher) {
            setError('Access denied. Only teachers can add or edit problems.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            // Sanitize the form data before processing
            const sanitizedForm = {
                ...problemForm,
                title: sanitizeText(problemForm.title),
                description: sanitizeText(problemForm.description),
                example: sanitizeText(problemForm.example),
                methodName: sanitizeText(problemForm.methodName),
                category: sanitizeText(problemForm.category)
            };

            const problemData = validateAndParseProblemForm(sanitizedForm);

            // Validate the generated/provided ID for security
            const idValidation = validateProblemId(problemData.id);
            if (!idValidation.valid) {
                throw new Error(`Invalid problem ID: ${idValidation.error}`);
            }

            const problemRef = doc(db, 'problems', problemData.id);

            // Check if category exists or create it
            if (problemData.category) {
                const categoryDocRef = doc(db, 'category_display_orders', problemData.category);
                const categorySnap = await getDoc(categoryDocRef);
                if (!categorySnap.exists()) {
                    const currentMaxCategoryOrder = categoryDisplayOrders.length > 0 ? Math.max(...categoryDisplayOrders.map(c => c.order || 0)) : -1;
                    await setDoc(categoryDocRef, {
                        name: problemData.category,
                        order: currentMaxCategoryOrder + 1,
                        createdAt: Timestamp.now()
                    });
                }
            }

            if (editMode) {
                // Update existing document
                await setDoc(problemRef, { ...problemData, updatedAt: Timestamp.now() }, { merge: true });
                setSuccessMessage(`Problem "${problemData.title}" updated successfully!`);
            } else {
                // Check if ID already exists for new problem
                const docSnap = await getDoc(problemRef);
                if (docSnap.exists()) {
                    throw new Error(`Problem with ID "${problemData.id}" already exists. Please choose a different title or edit the existing problem.`);
                }
                // For new problems, assign an order using hundreds-based system
                const positionInCategory = getNextPositionInCategory(problemData.category);
                const newOrder = calculateProblemOrder(problemData.category, positionInCategory);
                await setDoc(problemRef, { ...problemData, order: newOrder, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
                setSuccessMessage(`Problem "${problemData.title}" added successfully!`);
            }

            // Clear form and update problem list
            resetForm();
            onProblemAddedOrUpdated(); // Notify parent to refresh problems
            setEditMode(false);

        } catch (submitError) {
            console.error("Error saving problem:", submitError);
            setError(`Failed to save problem: ${submitError.message}`);
        } finally {
            setLoading(false);
        }
    }, [editMode, validateAndParseProblemForm, onProblemAddedOrUpdated, resetForm, problemForm, problemsList, categoryDisplayOrders, isTeacher, sanitizeText, validateProblemId]);

    const handleDeleteProblem = useCallback(async (problemIdToDelete) => {
        // Security check: Verify teacher permission
        if (!isTeacher) {
            setError('Access denied. Only teachers can delete problems.');
            return;
        }

        // Use a custom confirmation
        const isConfirmed = window.confirm(`Are you sure you want to delete problem "${problemIdToDelete}"? This action cannot be undone.`);

        if (!isConfirmed) {
            return; // User cancelled
        }

        setLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            await deleteDoc(doc(db, 'problems', problemIdToDelete));
            setSuccessMessage(`Problem "${problemIdToDelete}" deleted successfully!`);
            onProblemAddedOrUpdated(); // Notify parent to refresh problems
            if (problemForm.id === problemIdToDelete) { // If deleting the problem currently being edited
                resetForm();
            }
        } catch (deleteError) {
            console.error("Error deleting problem:", deleteError);
            setError(`Failed to delete problem: ${deleteError.message}`);
        } finally {
            setLoading(false);
        }
    }, [onProblemAddedOrUpdated, problemForm.id, resetForm, isTeacher]);

    // Function to move a problem up in order
    const handleMoveProblemUp = useCallback(async (problem) => {
        if (!isTeacher) {
            setError('Access denied. Only teachers can reorder problems.');
            return;
        }

        try {
            // Find problems in the same category
            const categoryProblems = problemsList
                .filter(p => (p.category || null) === (problem.category || null))
                .sort((a, b) => (a.order || 0) - (b.order || 0));

            const currentIndex = categoryProblems.findIndex(p => p.id === problem.id);

            if (currentIndex <= 0) {
                return; // Already at the top
            }

            const problemAbove = categoryProblems[currentIndex - 1];

            // Swap orders
            const problemRef = doc(db, 'problems', problem.id);
            const problemAboveRef = doc(db, 'problems', problemAbove.id);

            await setDoc(problemRef, { order: problemAbove.order }, { merge: true });
            await setDoc(problemAboveRef, { order: problem.order }, { merge: true });

            onProblemAddedOrUpdated(); // Refresh the list
        } catch (error) {
            console.error("Error moving problem up:", error);
            setError(`Failed to move problem: ${error.message}`);
        }
    }, [problemsList, isTeacher, onProblemAddedOrUpdated]);

    // Function to move a problem down in order
    const handleMoveProblemDown = useCallback(async (problem) => {
        if (!isTeacher) {
            setError('Access denied. Only teachers can reorder problems.');
            return;
        }

        try {
            // Find problems in the same category
            const categoryProblems = problemsList
                .filter(p => (p.category || null) === (problem.category || null))
                .sort((a, b) => (a.order || 0) - (b.order || 0));

            const currentIndex = categoryProblems.findIndex(p => p.id === problem.id);

            if (currentIndex === -1 || currentIndex >= categoryProblems.length - 1) {
                return; // Already at the bottom
            }

            const problemBelow = categoryProblems[currentIndex + 1];

            // Swap orders
            const problemRef = doc(db, 'problems', problem.id);
            const problemBelowRef = doc(db, 'problems', problemBelow.id);

            await setDoc(problemRef, { order: problemBelow.order }, { merge: true });
            await setDoc(problemBelowRef, { order: problem.order }, { merge: true });

            onProblemAddedOrUpdated(); // Refresh the list
        } catch (error) {
            console.error("Error moving problem down:", error);
            setError(`Failed to move problem: ${error.message}`);
        }
    }, [problemsList, isTeacher, onProblemAddedOrUpdated]);

    const handleBulkImport = useCallback(async () => {
        // Security check: Verify teacher permission before allowing import
        if (!isTeacher) {
            setBulkImportError('Access denied. Only teachers can perform bulk imports.');
            console.error('[Bulk Import] Unauthorized access attempt - user is not a teacher');
            return;
        }

        setBulkImportLoading(true);
        setBulkImportError(null);
        setBulkImportSuccess('');
        setError(null);

        try {
            console.log('[Bulk Import] Starting bulk import process...');

            // Security check: Limit JSON input size to prevent memory issues
            const inputSizeBytes = new Blob([bulkJsonInput]).size;
            if (inputSizeBytes > MAX_JSON_SIZE) {
                throw new Error(`JSON input is too large (${(inputSizeBytes / 1024 / 1024).toFixed(2)}MB). Maximum allowed size is ${MAX_JSON_SIZE / 1024 / 1024}MB. Please split your import into smaller batches.`);
            }
            console.log(`[Bulk Import] Input size: ${(inputSizeBytes / 1024).toFixed(2)}KB`);

            // Parse JSON input with proper error handling
            let problemsToImport;
            try {
                problemsToImport = JSON.parse(bulkJsonInput);
                console.log(`[Bulk Import] Successfully parsed JSON input`);
            } catch (jsonError) {
                // Provide detailed JSON parsing error with helpful context
                const errorPosition = jsonError.message.match(/position (\d+)/);
                const positionInfo = errorPosition ? ` at position ${errorPosition[1]}` : '';
                throw new Error(`Invalid JSON format${positionInfo}. Please check your JSON syntax. Error: ${jsonError.message}`);
            }

            // Validate that input is an array of problems
            if (!Array.isArray(problemsToImport)) {
                throw new Error(`JSON input must be an array of problems. Received: ${typeof problemsToImport}. Please wrap your problems in square brackets [].`);
            }

            if (problemsToImport.length === 0) {
                throw new Error("JSON array is empty. Please provide at least one problem to import.");
            }

            // Security check: Limit number of problems per import
            if (problemsToImport.length > MAX_PROBLEMS_PER_IMPORT) {
                throw new Error(`Too many problems in import (${problemsToImport.length}). Maximum allowed is ${MAX_PROBLEMS_PER_IMPORT} problems per import. Please split your import into smaller batches.`);
            }

            console.log(`[Bulk Import] Processing ${problemsToImport.length} problem(s)...`);

            let importedCount = 0;
            let updatedCount = 0;
            let failedCount = 0;
            const validationErrors = [];

            // Get the current max category order
            let currentMaxCategoryOrder = categoryDisplayOrders.length > 0 ? Math.max(...categoryDisplayOrders.map(c => c.order || 0)) : -1;

            // Calculate current maximum position within each category for automatic assignment
            // Using hundreds-based ordering system
            const categoryMaxPositions = {};
            problemsList.forEach(problem => {
                const category = problem.category || null;
                const baseOrder = getCategoryBaseOrder(category);
                const positionInCategory = (problem.order || 0) - baseOrder;
                if (!categoryMaxPositions.hasOwnProperty(category)) {
                    categoryMaxPositions[category] = positionInCategory;
                } else {
                    categoryMaxPositions[category] = Math.max(categoryMaxPositions[category], positionInCategory);
                }
            });

            // Iterate through problems and validate each one
            for (let i = 0; i < problemsToImport.length; i++) {
                const problem = problemsToImport[i];
                const problemIndex = i + 1; // 1-based index for user-friendly error messages

                try {
                    // Security: Sanitize text inputs to prevent XSS attacks
                    const sanitizedProblem = sanitizeProblem(problem);

                    // Validate individual problem structure using the dedicated validation function
                    const structureErrors = validateProblemStructure(sanitizedProblem);

                    if (structureErrors.length > 0) {
                        // Collect validation errors while continuing to process other problems
                        failedCount++;
                        const problemIdentifier = sanitizedProblem?.title || sanitizedProblem?.id || `Problem ${problemIndex}`;
                        validationErrors.push(`${problemIdentifier}: ${structureErrors.join(', ')}`);
                        continue; // Skip this problem and continue with the next one
                    }

                    // Determine the category for this problem (null for uncategorized)
                    const problemCategory = sanitizedProblem.category || null;

                    // Calculate order for this problem using hundreds-based system
                    // Respect explicit order values when provided (Requirement 6.2)
                    // Assign sequential orders starting from max + 1 for new problems within each category (Requirement 6.3)
                    let assignedOrder;
                    if (typeof sanitizedProblem.order === 'number') {
                        // Explicit order provided - use it
                        assignedOrder = sanitizedProblem.order;
                    } else {
                        // No explicit order - calculate next available position for this category
                        let positionInCategory;
                        if (!categoryMaxPositions.hasOwnProperty(problemCategory)) {
                            // First problem in this category
                            positionInCategory = 0;
                            categoryMaxPositions[problemCategory] = 0;
                        } else {
                            // Assign sequential position starting from max + 1 within category
                            positionInCategory = categoryMaxPositions[problemCategory] + 1;
                            categoryMaxPositions[problemCategory] = positionInCategory;
                        }
                        // Calculate full order using hundreds-based system
                        assignedOrder = calculateProblemOrder(problemCategory, positionInCategory);
                    }

                    // If validation passes, prepare the problem for form validation and parsing
                    const problemForValidation = {
                        id: sanitizedProblem.id || '',
                        title: sanitizedProblem.title,
                        description: sanitizedProblem.description,
                        example: sanitizedProblem.example,
                        templateCode: sanitizedProblem.templateCode,
                        methodName: sanitizedProblem.methodName,
                        tests: (sanitizedProblem.tests || []).map(t => ({
                            input: JSON.stringify(t.input),
                            expected: JSON.stringify(t.expected)
                        })),
                        category: sanitizedProblem.category || '',
                        order: assignedOrder
                    };

                    // Parse and validate the problem using existing form validation logic
                    let validatedProblem;
                    try {
                        validatedProblem = validateAndParseProblemForm(problemForValidation);
                    } catch (validationError) {
                        throw new Error(`Problem validation failed: ${validationError.message}`);
                    }

                    // Generate IDs from titles when not provided using existing logic
                    // (This is handled by validateAndParseProblemForm which generates ID from title if not provided)

                    // Check if categories exist in category_display_orders collection
                    // Create new category documents with appropriate ordering
                    // Maintain existing category display orders
                    // Handle uncategorized problems appropriately (null category is allowed)
                    if (validatedProblem.category) {
                        try {
                            const categoryDocRef = doc(db, 'category_display_orders', validatedProblem.category);
                            const categorySnap = await getDoc(categoryDocRef);
                            if (!categorySnap.exists()) {
                                // Create new category with next available order
                                currentMaxCategoryOrder++;
                                await setDoc(categoryDocRef, {
                                    name: validatedProblem.category,
                                    order: currentMaxCategoryOrder,
                                    createdAt: Timestamp.now()
                                });
                                console.log(`[Bulk Import] Created new category: ${validatedProblem.category} with order ${currentMaxCategoryOrder}`);
                            }
                        } catch (categoryError) {
                            // Log category creation error but don't fail the problem import
                            console.error(`[Bulk Import] Failed to create category "${validatedProblem.category}":`, categoryError);
                            // Category creation failure shouldn't prevent problem import
                        }
                    }

                    // Check for existing problems in Firestore
                    const problemRef = doc(db, 'problems', validatedProblem.id);
                    let docSnap;

                    try {
                        docSnap = await getDoc(problemRef);
                    } catch (firestoreReadError) {
                        throw new Error(`Failed to check if problem exists in database: ${firestoreReadError.message}`);
                    }

                    try {
                        if (docSnap.exists()) {
                            // Implement update logic for existing problems
                            // Preserve creation timestamps for updates
                            const existingData = docSnap.data();
                            await setDoc(problemRef, {
                                ...validatedProblem,
                                createdAt: existingData.createdAt || Timestamp.now(), // Preserve original createdAt
                                updatedAt: Timestamp.now()
                            }, { merge: true });
                            updatedCount++;
                            console.log(`[Bulk Import] Updated problem: ${validatedProblem.title} (ID: ${validatedProblem.id})`);
                        } else {
                            // Create new problem with timestamps
                            await setDoc(problemRef, {
                                ...validatedProblem,
                                createdAt: Timestamp.now(),
                                updatedAt: Timestamp.now()
                            });
                            importedCount++;
                            console.log(`[Bulk Import] Created new problem: ${validatedProblem.title} (ID: ${validatedProblem.id})`);
                        }
                    } catch (firestoreWriteError) {
                        throw new Error(`Failed to save problem to database: ${firestoreWriteError.message}`);
                    }
                } catch (processingError) {
                    // Collect processing errors while continuing to process other problems
                    failedCount++;
                    const problemIdentifier = sanitizeProblem(problem)?.title || sanitizeProblem(problem)?.id || `Problem ${problemIndex}`;
                    const errorMessage = `${problemIdentifier}: ${processingError.message}`;
                    validationErrors.push(errorMessage);
                    console.error(`[Bulk Import] Failed to process problem at index ${problemIndex}:`, {
                        problemIdentifier,
                        error: processingError.message
                    });
                }
            }

            // Provide detailed feedback on import results
            const totalProcessed = importedCount + updatedCount + failedCount;
            console.log(`[Bulk Import] Summary: Total=${totalProcessed}, Imported=${importedCount}, Updated=${updatedCount}, Failed=${failedCount}`);

            if (validationErrors.length > 0) {
                // Format error messages to be user-friendly and actionable
                const errorSummary = `Bulk import completed with errors:\n• ${failedCount} problem(s) failed\n• ${importedCount} new problem(s) imported\n• ${updatedCount} existing problem(s) updated\n\nFailed problems:\n• ${validationErrors.join('\n• ')}`;
                setBulkImportError(errorSummary);

                if (importedCount > 0 || updatedCount > 0) {
                    setBulkImportSuccess(`Partial success: ${importedCount} new problem(s) imported and ${updatedCount} existing problem(s) updated.`);
                }
            } else {
                // All problems processed successfully
                const successSummary = `Bulk import completed successfully!\n• ${importedCount} new problem(s) imported\n• ${updatedCount} existing problem(s) updated\n• Total: ${totalProcessed} problem(s) processed`;
                setBulkImportSuccess(successSummary);
            }

            // Clear input and refresh problem list on successful completion
            if (importedCount > 0 || updatedCount > 0) {
                setBulkJsonInput('');
                onProblemAddedOrUpdated();
                console.log('[Bulk Import] Problem list refreshed');
            }

        } catch (parseError) {
            // Handle top-level errors (JSON parsing, array validation, etc.)
            const errorMessage = `Failed to process bulk import: ${parseError.message}`;
            setBulkImportError(errorMessage);
            console.error('[Bulk Import] Top-level error:', {
                error: parseError.message,
                stack: parseError.stack
            });
        } finally {
            setBulkImportLoading(false);
            console.log('[Bulk Import] Import process completed');
        }
    }, [bulkJsonInput, validateProblemStructure, validateAndParseProblemForm, onProblemAddedOrUpdated, problemsList, categoryDisplayOrders, isTeacher, sanitizeProblem, MAX_JSON_SIZE, MAX_PROBLEMS_PER_IMPORT]);

    // Toggle category expansion in problem list
    const toggleCategoryExpand = useCallback((categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    }, []);

    // Group problems by category and sort categories based on categoryDisplayOrders
    const groupedAndOrderedProblems = useMemo(() => {
        const groups = {};
        problemsList.forEach(problem => {
            const category = problem.category || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(problem);
        });

        // Convert groups object to an array for sorting
        let groupedArray = Object.keys(groups).map(categoryName => ({
            categoryName,
            problems: groups[categoryName].sort((a, b) => {
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                return orderA - orderB;
            })
        }));

        // Sort categories based on the fetched categoryDisplayOrders
        groupedArray.sort((a, b) => {
            const orderA = categoryDisplayOrders.find(c => c.id === a.categoryName)?.order;
            const orderB = categoryDisplayOrders.find(c => c.id === b.categoryName)?.order;

            if (orderA === undefined && orderB === undefined) {
                return a.categoryName.localeCompare(b.categoryName);
            }
            if (orderA === undefined) return 1;
            if (orderB === undefined) return -1;
            return orderA - orderB;
        });

        return groupedArray;
    }, [problemsList, categoryDisplayOrders]);

    return (
        <Card className="bg-mad-gunmetal backdrop-blur-md border border-mad-dart-green shadow-lg rounded-xl text-mad-timberwolf">
            <CardHeader>
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <CardTitle className="text-lg sm:text-xl text-mad-fulvous flex items-center gap-2">
                            <Edit className="w-5 h-5 text-mad-fulvous" />
                            Problem Manager
                            {onClose && (
                                <Button
                                    onClick={onClose}
                                    variant="ghost"
                                    size="sm"
                                    className="ml-4 text-mad-peel hover:text-white hover:bg-mad-peel/20 rounded-full px-3 py-1 text-xs"
                                    title="Return to solve view"
                                >
                                    ← Back to Problems
                                </Button>
                            )}
                        </CardTitle>
                        <CardDescription className="text-mad-timberwolf">
                            Manage your coding problems. Add new problems, edit existing ones, reorder them, and organize by category.
                        </CardDescription>
                    </div>
                    {onClose && (
                        <Button
                            onClick={onClose}
                            variant="ghost"
                            size="sm"
                            className="text-mad-timberwolf hover:text-red-400 hover:bg-red-500/20 rounded-full -mt-1"
                        >
                            <XCircle className="w-5 h-5" />
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent className="space-y-6">
                {error && (
                    <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50 rounded-lg">
                        <XCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                {successMessage && (
                    <Alert variant="success" className="bg-green-500/10 text-green-400 border-green-500/50 rounded-lg">
                        <CheckCircle className="h-4 w-4" />
                        <AlertTitle>Success</AlertTitle>
                        <AlertDescription>{successMessage}</AlertDescription>
                    </Alert>
                )}

                {/* Existing Problems Section */}
                <div className="mt-4 pt-0">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-md font-semibold text-mad-timberwolf">Existing Problems</h3>
                        <Button
                            onClick={() => setShowBulkImport(!showBulkImport)}
                            variant="outline"
                            size="sm"
                            className="text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-white rounded-full"
                        >
                            <Upload className="w-4 h-4 mr-1" /> {showBulkImport ? 'Hide Bulk Import' : 'Bulk Import'}
                        </Button>
                    </div>

                    {showBulkImport && (
                        <div className="bg-mad-gunmetal/80 p-4 rounded-lg space-y-4 mb-6 border border-mad-dart-green">
                            <h4 className="text-sm font-semibold text-mad-timberwolf">Import Problems from JSON Array</h4>
                            <Textarea
                                value={bulkJsonInput}
                                onChange={(e) => setBulkJsonInput(e.target.value)}
                                placeholder={`[
  {
    "id": "reverse-string",
    "title": "Reverse String",
    "description": "Write a method that takes a string and returns it reversed. This is a fundamental string manipulation problem.",
    "example": "reverseString(\\"hello\\") → \\"olleh\\"\\nreverseString(\\"world\\") → \\"dlrow\\"",
    "templateCode": "    public String reverseString(String str) {\\n        // Your code here\\n        return \\"\\";\\n    }",
    "methodName": "reverseString",
    "category": "Strings",
    "order": 1,
    "tests": [
      { "input": ["hello"], "expected": "olleh" },
      { "input": ["world"], "expected": "dlrow" },
      { "input": [""], "expected": "" },
      { "input": ["a"], "expected": "a" }
    ]
  },
  {
    "title": "Sum Two Numbers",
    "description": "Write a method that takes two integers and returns their sum.",
    "example": "sumTwoNumbers(3, 5) → 8\\nsumTwoNumbers(-1, 1) → 0",
    "templateCode": "    public int sumTwoNumbers(int a, int b) {\\n        // Your code here\\n        return 0;\\n    }",
    "methodName": "sumTwoNumbers",
    "category": "Math",
    "tests": [
      { "input": [3, 5], "expected": 8 },
      { "input": [-1, 1], "expected": 0 },
      { "input": [0, 0], "expected": 0 },
      { "input": [100, -50], "expected": 50 }
    ]
  }
]

Required fields: title, description, example, templateCode, methodName, tests
Optional fields: id (auto-generated from title if not provided), category, order (auto-assigned if not provided)`}
                                className="min-h-[300px] bg-black/80 text-white border-mad-dart-green font-mono text-xs rounded-md resize-y"
                            />
                            <Button
                                onClick={handleBulkImport}
                                disabled={bulkImportLoading || !bulkJsonInput.trim()}
                                className={cn(
                                    "bg-mad-fulvous text-white w-full rounded-full",
                                    "hover:bg-mad-fulvous/80 transition-colors duration-200",
                                    "disabled:opacity-50 disabled:cursor-not-allowed"
                                )}
                            >
                                {bulkImportLoading ? (
                                    <>
                                        <Loader2 className="animate-spin w-4 h-4 mr-2" /> Importing...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" /> Import Problems
                                    </>
                                )}
                            </Button>
                            {bulkImportError && (
                                <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50 rounded-lg">
                                    <XCircle className="h-4 w-4" />
                                    <AlertTitle>Bulk Import Error</AlertTitle>
                                    <AlertDescription className="whitespace-pre-line">{bulkImportError}</AlertDescription>
                                </Alert>
                            )}
                            {bulkImportSuccess && (
                                <Alert variant="success" className="bg-green-500/10 text-green-400 border-green-500/50 rounded-lg">
                                    <CheckCircle className="h-4 w-4" />
                                    <AlertTitle>Bulk Import Success</AlertTitle>
                                    <AlertDescription className="whitespace-pre-line">{bulkImportSuccess}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                    <ul className="space-y-4 overflow-y-auto max-h-[600px] pr-2 pt-4">
                        {problemsList.length === 0 ? (
                            <p className="text-mad-timberwolf italic">No problems found. Add a new one!</p>
                        ) : (
                            groupedAndOrderedProblems.map(group => (
                                <li key={group.categoryName} className="mb-2">
                                    <div className="flex justify-between items-center bg-mad-dart-green/70 text-white px-3 py-2 rounded-md font-semibold mb-2">
                                        <span className="flex items-center gap-2">
                                            {group.categoryName} ({group.problems.length})
                                            <Button type="button" onClick={() => toggleCategoryExpand(group.categoryName)} variant="ghost" size="icon" className="text-white hover:bg-white/20 rounded-full">
                                                {expandedCategories[group.categoryName] ? <ChevronUp /> : <ChevronDown />}
                                            </Button>
                                        </span>
                                    </div>

                                    {expandedCategories[group.categoryName] && (
                                        <ul className="space-y-2 pl-4">
                                            {group.problems.map((problem) => (
                                                <li key={problem.id} className="flex justify-between items-center bg-mad-gunmetal/80 p-3 rounded-lg border border-mad-dart-green">
                                                    <span className="text-mad-timberwolf font-medium">{problem.title} <span className="text-mad-peel text-sm">- Order: {problem.order}</span></span>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            onClick={() => handleMoveProblemUp(problem)}
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-white rounded-full"
                                                            title="Move Up"
                                                        >
                                                            <ArrowUp className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleMoveProblemDown(problem)}
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-white rounded-full"
                                                            title="Move Down"
                                                        >
                                                            <ArrowDown className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleEditProblemClick(problem)}
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-mad-fulvous border-mad-fulvous hover:bg-mad-fulvous/20 hover:text-white rounded-full"
                                                            title="Edit"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleDeleteProblem(problem.id)}
                                                            variant="destructive"
                                                            size="sm"
                                                            className="text-red-300 border-red-300 hover:bg-red-300/20 hover:text-red-100 rounded-full"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))
                        )}
                    </ul>
                </div>

                {/* Add/Edit Problem Form */}
                <div className="mt-8 pt-4 border-t border-mad-dart-green">
                    <CardTitle className="text-lg sm:text-xl text-mad-fulvous flex items-center gap-2 mb-4">
                        <Plus className="w-5 h-5 text-mad-fulvous" />
                        {editMode ? 'Edit Problem' : 'Add New Problem'}
                    </CardTitle>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!editMode && (
                            <div>
                                <label htmlFor="id" className="block text-sm font-medium text-mad-timberwolf mb-1">Problem ID (auto-generated from title, can be manually overridden):</label>
                                <Input
                                    id="id"
                                    name="id"
                                    type="text"
                                    value={problemForm.id}
                                    onChange={handleChange}
                                    placeholder="Auto-generated from title (e.g., sum-two-numbers)"
                                    className="bg-black/80 text-white border-mad-dart-green rounded-md"
                                    disabled={editMode}
                                />
                            </div>
                        )}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-mad-timberwolf mb-1">Problem Title:</label>
                            <Input
                                id="title"
                                name="title"
                                type="text"
                                value={problemForm.title}
                                onChange={handleChange}
                                placeholder="e.g., Sum Two Numbers"
                                className="bg-black/80 text-white border-mad-dart-green rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-mad-timberwolf mb-1">Problem Description:</label>
                            <Textarea
                                id="description"
                                name="description"
                                value={problemForm.description}
                                onChange={handleChange}
                                placeholder="Describe the problem clearly..."
                                className="min-h-[100px] bg-black/80 text-white border-mad-dart-green rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="example" className="block text-sm font-medium text-mad-timberwolf mb-1">Example:</label>
                            <Input
                                id="example"
                                name="example"
                                type="text"
                                value={problemForm.example}
                                onChange={handleChange}
                                placeholder="e.g., sum(2, 3) → 5"
                                className="bg-black/80 text-white border-mad-dart-green rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-mad-timberwolf mb-1">Category (optional):</label>
                            <Input
                                id="category"
                                name="category"
                                type="text"
                                value={problemForm.category}
                                onChange={handleChange}
                                placeholder="e.g., Math, Strings, Arrays"
                                className="bg-black/80 text-white border-mad-dart-green rounded-md"
                            />
                        </div>
                        <div>
                            <label htmlFor="methodName" className="block text-sm font-medium text-mad-timberwolf mb-1">Method Name:</label>
                            <Input
                                id="methodName"
                                name="methodName"
                                type="text"
                                value={problemForm.methodName}
                                onChange={handleChange}
                                placeholder="e.g., sum"
                                className="bg-black/80 text-white border-mad-dart-green rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="templateCode" className="block text-sm font-medium text-mad-timberwolf mb-1">Template Code:</label>
                            <Textarea
                                id="templateCode"
                                name="templateCode"
                                value={problemForm.templateCode}
                                onChange={handleChange}
                                placeholder="public static int sum(int a, int b) { /* your code */ return 0; }"
                                className="min-h-[150px] bg-black/80 text-white border-mad-dart-green font-mono text-sm rounded-md"
                                required
                            />
                        </div>

                        {/* Compact Test Cases Section */}
                        <div className="space-y-3 p-4 border border-mad-dart-green rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-md font-semibold text-mad-timberwolf">Test Cases</h3>
                                <Button type="button" onClick={addTestCase} variant="outline" size="sm"
                                    className="text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-white rounded-full">
                                    <Plus className="w-4 h-4 mr-1" /> Add Test
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {problemForm.tests.map((test, index) => (
                                    <div key={index} className="flex items-center gap-3 p-3 bg-mad-gunmetal/80 border border-mad-dart-green rounded-lg">
                                        <div className="flex-shrink-0 w-16">
                                            <span className="text-sm font-medium text-mad-timberwolf">Case {index + 1}:</span>
                                        </div>
                                        <div className="flex-1">
                                            <label htmlFor={`test-input-${index}`} className="block text-xs font-medium text-mad-timberwolf mb-1">Input:</label>
                                            <Input
                                                id={`test-input-${index}`}
                                                type="text"
                                                value={test.input}
                                                onChange={(e) => handleTestCaseChange(index, 'input', e.target.value)}
                                                placeholder={"[5, 3] or 10"}
                                                className="bg-black/80 text-white border-mad-dart-green text-xs font-mono rounded-md"
                                                required
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label htmlFor={`test-expected-${index}`} className="block text-xs font-medium text-mad-timberwolf mb-1">Expected:</label>
                                            <Input
                                                id={`test-expected-${index}`}
                                                type="text"
                                                value={test.expected}
                                                onChange={(e) => handleTestCaseChange(index, 'expected', e.target.value)}
                                                placeholder={"8 or true"}
                                                className="bg-black/80 text-white border-mad-dart-green text-xs font-mono rounded-md"
                                                required
                                            />
                                        </div>
                                        <div className="flex-shrink-0">
                                            <Button
                                                type="button"
                                                onClick={() => removeTestCase(index)}
                                                variant="destructive"
                                                size="sm"
                                                className="text-red-300 border-red-300 hover:bg-red-300/20 hover:text-red-100 rounded-full px-2"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                type="button"
                                onClick={resetForm}
                                variant="outline"
                                className="text-mad-timberwolf border-mad-timberwolf hover:bg-mad-timberwolf/20 hover:text-white rounded-full"
                                disabled={loading}
                            >
                                Cancel / New Problem
                            </Button>
                            <Button
                                type="submit"
                                className={cn(
                                    "bg-mad-fulvous text-white px-6 py-3 rounded-full shadow-lg",
                                    "hover:bg-mad-fulvous/80 transition-colors duration-200",
                                    "disabled:opacity-50 disabled:cursor-not-allowed"
                                )}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="animate-spin w-4 h-4 mr-2" /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" /> {editMode ? 'Update Problem' : 'Add Problem'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </CardContent>
        </Card>
    );
};

export default ProblemManager;